import * as React from 'react';
import styled, { useTheme } from 'styled-components';

import useMediaQuery from '@/utils/useMediaQuery';

import { CodeIcon } from '../../icons/Icons';
import { ReactComponent as AnnotationLineSVG } from '../../images/notebooks/annotation-line.svg';
import { ANIMATIONS, DOCS_LINKS } from '../../utils';
import { Heading, Text } from '../design-system';
import { InfoText } from '../global/InfoText';
import { ProductIllustration } from '../global/ProductIllustration';
import {
  CellIllustration,
  FloatingText,
  Section
} from '../global/sharedStyles';

const Wrapper = styled(Section)`
  display: flex;
  flex-direction: column;
  height: 673px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    && {
      height: auto;
      gap: 20px;
    }
  }

  .info-text {
    max-width: 440px;

    @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
      max-width: 100%;
    }
  }
`;

const TypeaheadIllustration = styled(CellIllustration)`
  top: 484px;
  left: 672px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
  }
`;

const SQLCellIllustration = styled(ProductIllustration)`
  top: 280px;
  left: 240px;
  flex: none;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    &&&&& {
      width: 70%;
      min-width: 370px;
      top: auto;
      left: auto;
      position: static !important;

      img {
        width: 100% !important;
        height: 100% !important;
      }
    }
  }
`;

const PythonCellIllustration = styled(ProductIllustration)`
  top: 30px;
  right: 60px;
  flex: none;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    &&&&& {
      width: 70%;
      min-width: 370px;
      top: auto;
      right: auto;
      position: static !important;

      img {
        width: 100% !important;
        height: 100% !important;
      }
    }
  }
`;

const SubText = styled.div`
  position: absolute;
  z-index: 9;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    && {
      position: static;
      max-width: 100%;
      min-width: 60%;
    }
  }
`;

const SubTextTitle = styled(Heading).attrs({ renderAs: 'h4' })`
  margin-bottom: 10px;
`;

const SubTextDesc = styled(Text).attrs({ size: 'medium' })`
  margin-bottom: 8px;

  ul {
    padding-left: 18px;
    list-style-type: circle;
  }
`;

const SubTextSQL = styled(SubText)`
  top: 380px;
  left: 0;
  max-width: 220px;
`;

const SubTextPython = styled(SubText)`
  top: 390px;
  right: -50px;
  max-width: 260px;
`;

const CellContent = styled.div`
  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: flex;
    align-items: center;
    gap: 30px;
  }
`;

const AnnotationLine = styled(AnnotationLineSVG)`
  position: relative;
  z-index: 1;
  pointer-events: none;
  position: absolute;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
  }
`;

const AnnotationLineSQL = styled(AnnotationLine)`
  top: 387px;
  left: 200px;
`;

const AnnotationLinePython = styled(AnnotationLine)`
  top: 349px;
  right: 169px;
  transform: rotate(-90deg);
`;

export const SectionSQLPython: React.ComponentType = React.memo(() => {
  const theme = useTheme();
  const shouldCollapse = useMediaQuery(theme.mediaQueryString.MEDIUM);

  const sqlSubtext = !shouldCollapse ? (
    <SubTextSQL>
      <SubTextTitle renderAs="h4">SQL superpowers</SubTextTitle>
      <SubTextDesc>
        A first-class query editor, right in your notebook, including:
      </SubTextDesc>
      <SubTextDesc>
        <ul>
          <li>Jinja support</li>
          <li>dbt integration</li>
          <li>Schema browser</li>
          <li>Typeahead</li>
          <li>Syntax highlighting</li>
        </ul>
      </SubTextDesc>
    </SubTextSQL>
  ) : (
    <SubTextSQL>
      <SubTextTitle renderAs="h4">SQL superpowers</SubTextTitle>
      <SubTextDesc>
        A first-class query editor, right in your notebook, including: Jinja
        support, dbt integration, schema browser, typeahead, and syntax
        highlighting.
      </SubTextDesc>
    </SubTextSQL>
  );

  return (
    <Wrapper>
      <FloatingText>
        <InfoText
          description="With Hex, you don’t have to choose. Use SQL for what it’s great at, and then layer in Python for extra flexibility and power."
          icon={<CodeIcon />}
          link={DOCS_LINKS.SQLCells}
          title="SQL and Python, together in harmony"
        />
      </FloatingText>
      <CellContent>
        {sqlSubtext}
        <SQLCellIllustration
          cellLabel="SQL cell"
          imgPath="notebooks/sql-cell"
          outputName="dataframe"
        />
        <TypeaheadIllustration {...ANIMATIONS.Cell}>
          <img
            alt="SQL typeahead"
            src="/images/notebooks/sql-typeahead.png"
            srcSet={`/images/notebooks/sql-typeahead.png 1x, /images/notebooks/sql-typeahead@2x.png 2x`}
          />
        </TypeaheadIllustration>
        <AnnotationLineSQL />
      </CellContent>
      <CellContent>
        <SubTextPython>
          <SubTextTitle renderAs="h4">IDE-class Python and R</SubTextTitle>
          <SubTextDesc>
            Integrated package management, formatting, and typeahead make
            editing a breeze.
          </SubTextDesc>
        </SubTextPython>
        <PythonCellIllustration
          cellLabel="Python cell"
          imgPath="notebooks/python-cell"
          showCommentCount={true}
        />
        <AnnotationLinePython />
      </CellContent>
    </Wrapper>
  );
});
